// Orchestrateur de préchargement des pages
import { LoadingQueueService, TaskPriority } from './LoadingQueueService';
import { LazyLoadLogger } from './LazyLoadLogger';
import { MediaService } from './MediaService';
import config, { type Config, type LayoutItem } from '../Config/config';
import { isConfigLocalStorageEnabled } from '../Config/devMode';

export interface PreloadStatus {
    isPreloading: boolean;
    isPaused: boolean;
    pagesPreloaded: number;
    pagesTotal: number;
    currentPage: string | null;
}

class PagePreloadOrchestratorClass {
    private status: PreloadStatus = {
        isPreloading: false,
        isPaused: false,
        pagesPreloaded: 0,
        pagesTotal: 0,
        currentPage: null
    };

    private preloadedUrls = new Set<string>();

    /**
     * Démarre le préchargement des pages depuis la page actuelle
     */
    async startPreloading(
        currentPageUrl: string,
        config: Config,
        pageLoader: (url: string) => Promise<LayoutItem[]>,
        onComplete?: () => void
    ): Promise<void> {
        // Si déjà en cours, ne pas redémarrer
        if (this.status.isPreloading) {
            LazyLoadLogger.logPreload('Préchargement déjà en cours');
            return;
        }

        this.status.isPreloading = true;
        this.status.isPaused = false;
        this.status.pagesPreloaded = 0;

        // Analyser les pages à précharger
        const preloadablePages = this.getPreloadablePages(currentPageUrl, config);

        this.status.pagesTotal = preloadablePages.length;

        LazyLoadLogger.group('preload', `📄 Préchargement de ${preloadablePages.length} pages accessibles`, true);
        LazyLoadLogger.logPreload(`Page actuelle: ${currentPageUrl}`);
        LazyLoadLogger.table('preload', 'Pages à précharger', preloadablePages);
        LazyLoadLogger.groupEnd();

        // Ajouter les tâches à la queue
        for (const page of preloadablePages) {
            // Si déjà préchargée, ignorer
            if (this.preloadedUrls.has(page.url)) {
                continue;
            }

            LoadingQueueService.enqueue({
                id: `preload-page-${page.url}`,
                priority: TaskPriority.P2_PAGES,
                execute: async () => {
                    this.status.currentPage = page.url;
                    
                    LazyLoadLogger.time(`Préchargement ${page.url}`);
                    LazyLoadLogger.logPreload(`🔄 Préchargement viewport d'arrivée: ${page.url}`, {
                        priority: page.priority,
                        source: page.source
                    });

                    try {
                        // Charger les instances de la page depuis l'API
                        const pageItems = await pageLoader(page.url);
                        
                        LazyLoadLogger.logPreload(`📦 Page ${page.url}: ${pageItems.length} composants chargés depuis l'API`, {
                            total: pageItems.length,
                            composants: pageItems.map(i => i.component)
                        });
                        
                        // Précharger les médias de tous les composants dans le cache du navigateur
                        await this.preloadViewportComponents(pageItems, page.url);
                        
                        this.preloadedUrls.add(page.url);
                        this.status.pagesPreloaded++;

                        LazyLoadLogger.timeEnd(`Préchargement ${page.url}`);
                        LazyLoadLogger.logPreload(`✅ Page préchargée: ${page.url} (${this.status.pagesPreloaded}/${this.status.pagesTotal})`);
                    } catch (error) {
                        LazyLoadLogger.error('preload', `❌ Erreur préchargement: ${page.url}`, error);
                    }
                }
            });
        }

        LazyLoadLogger.logPreload(`📋 ${preloadablePages.length} pages ajoutées à la queue de préchargement`);

        // Surveiller la fin du préchargement
        this.waitForCompletion(() => {
            this.status.isPreloading = false;
            this.status.currentPage = null;
            
            LazyLoadLogger.logPreload(`✅ Préchargement terminé: ${this.status.pagesPreloaded}/${this.status.pagesTotal} pages`);
            
            if (onComplete) {
                onComplete();
            }
        });
    }

    /**
     * Précharge les composants du viewport d'une page
     */
    private async preloadViewportComponents(items: LayoutItem[], pageUrl: string): Promise<void> {
        LazyLoadLogger.group('preload', `🎯 Préchargement médias de ${pageUrl}`, true);
        
        for (const item of items) {
            try {
                LazyLoadLogger.logPreload(`  ├─ ${item.component}`, {
                    id: item.id,
                    props: Object.keys(item.props || {})
                });
                
                // Précharger selon le type de composant
                if (item.component === 'ImageModule') {
                    await this.preloadImageComponent(item);
                } else if (item.component === 'VideoModule') {
                    await this.preloadVideoComponent(item);
                } else {
                    // Autres composants : juste logger
                    LazyLoadLogger.logPreload(`    └─ Composant standard (pas de préchargement média)`);
                }
                
            } catch (error) {
                LazyLoadLogger.warn('preload', `    └─ Erreur préchargement ${item.component}`, error);
            }
        }
        
        LazyLoadLogger.groupEnd();
    }

    /**
     * Précharge une image
     */
    private async preloadImageComponent(item: LayoutItem): Promise<void> {
        const fileId = item.props?.fileId as string | undefined;
        const src = item.props?.src as string | undefined;
        
        if (!fileId && !src) {
            LazyLoadLogger.logPreload(`    └─ Pas de source image`);
            return;
        }
        
        const source = fileId || src;
        
        // Créer une Image pour précharger
        return new Promise((resolve) => {
            const img = new Image();
            
            img.onload = () => {
                LazyLoadLogger.logPreload(`    └─ ✅ Image préchargée`, { source });
                resolve();
            };
            
            img.onerror = () => {
                LazyLoadLogger.warn('preload', `    └─ ⚠️ Erreur préchargement image`, { source });
                resolve(); // Ne pas bloquer si erreur
            };
            // Construire l'URL (simplifiée pour préchargement)
            if (source?.startsWith('/') || source?.startsWith('http')) {
                img.src = source;
            } else if (fileId) {
                // URL API avec qualité normale pour préchargement efficace
                img.src = MediaService.getImageUrl(fileId, { quality: 85, width: 1920 });
            }
        });
    }

    /**
     * Précharge une vidéo (juste le manifest HLS)
     */
    private async preloadVideoComponent(item: LayoutItem): Promise<void> {
        const fileId = item.props?.fileId as string | undefined;
        if (!fileId) {
            LazyLoadLogger.logPreload('    └─ Pas de fileId vidéo');
            return;
        }

        const TIMEOUT_MS = 5000;
        const RANGE_BYTES = 64 * 1024; // 64KB
        const qualityPref = item.props?.quality as ('low' | 'medium' | 'high' | number | undefined);

        // Récupération flexible du read_token (supporte plusieurs noms)
        const readToken = config.api.readToken;

        const appendReadToken = (rawUrl: string) => {
            try {
                const u = new URL(rawUrl);
                if (readToken) u.searchParams.set('read_token', String(readToken));
                return u.href;
            } catch {
                // si URL invalide, fallback : concat simple (encode)
                if (!readToken) return rawUrl;
                const sep = rawUrl.includes('?') ? '&' : '?';
                return `${rawUrl}${sep}read_token=${encodeURIComponent(String(readToken))}`;
            }
        };

        const parseAttrs = (line: string): Record<string, string> => {
            const idx = line.indexOf(':');
            const attrs = idx >= 0 ? line.slice(idx + 1) : line;
            const parts: string[] = [];
            let cur = '';
            let inQ = false;
            for (let i = 0; i < attrs.length; i++) {
                const ch = attrs[i];
                if (ch === '"' && attrs[i - 1] !== '\\') inQ = !inQ;
                if (ch === ',' && !inQ) { parts.push(cur); cur = ''; } else cur += ch;
            }
            if (cur) parts.push(cur);
            const res: Record<string, string> = {};
            for (const p of parts) {
                const eq = p.indexOf('=');
                if (eq > -1) {
                    const k = p.slice(0, eq).trim();
                    let v = p.slice(eq + 1).trim();
                    if (v.startsWith('"') && v.endsWith('"')) v = v.slice(1, -1);
                    res[k] = v;
                }
            }
            return res;
        };

        const chooseVariant = (variants: Array<{ uri: string; bw: number }>) => {
            if (!variants.length) return null;
            if (qualityPref === 'low') {
                return variants.reduce((a, b) => (a.bw <= b.bw ? a : b));
            }
            if (qualityPref === 'high') {
                return variants.reduce((a, b) => (a.bw >= b.bw ? a : b));
            }
            if (qualityPref === 'medium') {
                const sorted = variants.slice().sort((x, y) => x.bw - y.bw);
                return sorted[Math.floor((sorted.length - 1) / 2)];
            }
            if (typeof qualityPref === 'number') {
                let best = variants[0];
                let bestDiff = Math.abs(variants[0].bw - qualityPref);
                for (const v of variants) {
                    const d = Math.abs(v.bw - qualityPref);
                    if (d < bestDiff) { best = v; bestDiff = d; }
                }
                return best;
            }
            // default : highest
            return variants.reduce((a, b) => (a.bw >= b.bw ? a : b));
        };

        try {
            const rawManifestUrl = MediaService.getHLSUrl(fileId);
            const manifestUrl = appendReadToken(rawManifestUrl);
            LazyLoadLogger.logPreload(`    └─ Manifest: ${manifestUrl}`, { fileId, readTokenPresent: !!readToken });

            // GET master/manifest (avec read_token dans la query si présent)
            const manifestResp = await fetch(manifestUrl, { signal: (AbortSignal as any).timeout(TIMEOUT_MS) });
            if (!manifestResp.ok) {
                LazyLoadLogger.warn('preload', `    └─ ⚠️ Manifest status ${manifestResp.status}`, { fileId });
                return;
            }
            const manifestText = await manifestResp.text();

            const isMaster = manifestText.includes('#EXT-X-STREAM-INF');
            let variantPlaylistUrlRaw: string | null = null;

            if (isMaster) {
                const lines = manifestText.split(/\r?\n/).map(l => l.trim());
                const variants: Array<{ uri: string; bw: number }> = [];
                for (let i = 0; i < lines.length; i++) {
                    const ln = lines[i];
                    if (ln.startsWith('#EXT-X-STREAM-INF')) {
                        const attrs = parseAttrs(ln);
                        const next = lines[i + 1];
                        if (next && !next.startsWith('#')) {
                            // construis l'URI relative à partir du manifest original (rawManifestUrl)
                            const uri = new URL(next, rawManifestUrl).href;
                            const bw = Number(attrs.BANDWIDTH || 0);
                            variants.push({ uri, bw });
                        }
                    }
                }
                if (variants.length === 0) {
                    LazyLoadLogger.warn('preload', '    └─ ⚠️ Master sans variantes', { fileId });
                } else {
                    const chosen = chooseVariant(variants);
                    if (!chosen) {
                        LazyLoadLogger.warn('preload', '    └─ ⚠️ Aucune variante choisie', { fileId });
                        return;
                    }
                    variantPlaylistUrlRaw = chosen.uri;
                    LazyLoadLogger.logPreload(`    └─ Variante choisie (bw=${chosen.bw}): ${variantPlaylistUrlRaw}`, { fileId });
                }
            }

            // get media playlist (either the manifest itself or the chosen variant) — ensure token appended
            let mediaText = manifestText;
            let variantPlaylistUrlWithToken: string | null = null;
            if (variantPlaylistUrlRaw) {
                variantPlaylistUrlWithToken = appendReadToken(variantPlaylistUrlRaw);
                const vResp = await fetch(variantPlaylistUrlWithToken, { signal: (AbortSignal as any).timeout(TIMEOUT_MS) });
                if (!vResp.ok) {
                    LazyLoadLogger.warn('preload', `    └─ ⚠️ Variante status ${vResp.status}`, { fileId, variant: variantPlaylistUrlWithToken });
                    return;
                }
                mediaText = await vResp.text();
            }

            // find first segment
            const mediaLines = mediaText.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
            let firstSeg: string | undefined;
            for (let i = 0; i < mediaLines.length; i++) {
                const ln = mediaLines[i];
                if (!ln.startsWith('#') && /\.(ts|m4s|mp4|aac)(\?.*)?$/i.test(ln)) { firstSeg = ln; break; }
                if (ln.startsWith('#EXTINF')) {
                    const next = mediaLines[i + 1];
                    if (next && !next.startsWith('#')) { firstSeg = next; break; }
                }
            }
            if (!firstSeg) {
                LazyLoadLogger.warn('preload', '    └─ ⚠️ Aucun segment trouvé', { fileId });
                return;
            }

            // build segment URL relative to the variant playlist if present, else to manifest
            const baseForSegment = variantPlaylistUrlRaw || rawManifestUrl;
            const segRawUrl = new URL(firstSeg, baseForSegment).href;
            const segUrl = appendReadToken(segRawUrl);
            LazyLoadLogger.logPreload(`    └─ Premier segment: ${segUrl}`, { fileId });

            // GET partial (Range) to pre-warm cache — GET only (no HEAD)
            try {
                const segResp = await fetch(segUrl, {
                    method: 'GET',
                    headers: { Range: `bytes=0-${RANGE_BYTES - 1}` },
                    signal: (AbortSignal as any).timeout(TIMEOUT_MS + 2000)
                });
                if (segResp.ok || segResp.status === 206) {
                    await segResp.arrayBuffer(); // consume small chunk
                    LazyLoadLogger.logPreload('    └─ ✅ Premier segment préchargé (GET range)', { fileId });
                } else {
                    LazyLoadLogger.warn('preload', `    └─ ⚠️ GET segment status ${segResp.status}`, { fileId, segUrl });
                }
            } catch (segErr) {
                LazyLoadLogger.warn('preload', `    └─ ⚠️ Erreur GET segment: ${segErr instanceof Error ? segErr.message : String(segErr)}`, { fileId, segUrl });
            }
        } catch (err) {
            LazyLoadLogger.warn('preload', `    └─ ⚠️ Erreur préchargement HLS: ${err instanceof Error ? err.message : String(err)}`, { fileId });
        }
    }



    /**
     * Attend la fin du préchargement
     */
    private waitForCompletion(callback: () => void): void {
        const checkInterval = setInterval(() => {
            const stats = LoadingQueueService.getStats();
            
            // Vérifier si toutes les tâches de préchargement sont terminées
            const hasPreloadTasks = stats.pending > 0 || stats.running > 0;
            
            if (!hasPreloadTasks) {
                clearInterval(checkInterval);
                callback();
            }
        }, 500);
    }

    /**
     * Met en pause le préchargement si la charge est élevée
     */
    pauseIfOverloaded(): void {
        if (this.status.isPaused) return;

        const stats = LoadingQueueService.getStats();
        
        // Si trop de tâches en cours ou en attente
        if (stats.running >= 3 || stats.pending >= 10) {
            this.status.isPaused = true;
            LoadingQueueService.pause();
            
            LazyLoadLogger.warn('preload', 'Préchargement mis en pause (charge élevée)', stats);
        }
    }

    /**
     * Reprend le préchargement
     */
    resume(): void {
        if (!this.status.isPaused) return;

        this.status.isPaused = false;
        LoadingQueueService.resume();
        
        LazyLoadLogger.logPreload('Préchargement repris');
    }

    /**
     * Arrête le préchargement
     */
    stop(): void {
        this.status.isPreloading = false;
        this.status.isPaused = false;
        this.status.currentPage = null;
        
        // Annuler toutes les tâches de préchargement en attente
        LoadingQueueService.cancelByPriority(TaskPriority.P2_PAGES);
        
        LazyLoadLogger.logPreload('Préchargement arrêté');
    }

    /**
     * Récupère le statut du préchargement
     */
    getStatus(): PreloadStatus {
        return { ...this.status };
    }

    /**
     * Réinitialise l'orchestrateur (changement de page)
     */
    reset(): void {
        this.stop();
        // Ne PAS effacer preloadedUrls afin de conserver l'historique des pages déjà préchargées
        // Cela évite de relancer indéfiniment le préchargement lors de chaque navigation.
        this.status.pagesPreloaded = this.preloadedUrls.size;
        this.status.pagesTotal = this.preloadedUrls.size;
        
        LazyLoadLogger.logPreload('Orchestrateur réinitialisé');
    }

    /**
     * Vérifie si une page a été préchargée
     */
    isPagePreloaded(url: string): boolean {
        return this.preloadedUrls.has(url);
    }

    /**
     * Démarre la surveillance automatique de la charge
     * Met en pause/reprend selon la charge du système
     */
    startAutoLoadMonitoring(): void {
        LoadingQueueService.startLoadMonitoring();
        LazyLoadLogger.logPreload('Surveillance automatique de charge activée');
    }

    /**
     * Arrête la surveillance automatique
     */
    stopAutoLoadMonitoring(): void {
        LoadingQueueService.stopLoadMonitoring();
        LazyLoadLogger.logPreload('Surveillance automatique de charge désactivée');
    }

    /**
     * Récupère les pages à précharger depuis l'éditeur de config
     */
    private getPreloadablePages(currentPageUrl: string, config: Config): Array<{url: string, priority: number, source: string}> {
        const pages: Array<{url: string, priority: number, source: string}> = [];
        const seenUrls = new Set<string>();

        // Récupérer les pages depuis l'éditeur de config (localStorage)
        let allPages = config.PagesList;
        try {
            const savedConfig = isConfigLocalStorageEnabled(config) ? localStorage.getItem('gandc_config_backup') : null;
            if (savedConfig) {
                const parsedConfig = JSON.parse(savedConfig);
                if (parsedConfig.PagesList && Array.isArray(parsedConfig.PagesList)) {
                    allPages = parsedConfig.PagesList;
                    console.log(`📄 Pages trouvées dans l'éditeur: ${allPages.length}`);
                }
            }
        } catch (error) {
            console.warn('⚠️ Erreur lors de la récupération des pages depuis localStorage:', error);
        }

        // Ajouter les pages de la navbar (priorité haute)
        const navbarPages = allPages.filter(
            page => page.InNavBar && page.pageUrl !== currentPageUrl
        );

        for (const page of navbarPages) {
            if (!seenUrls.has(page.pageUrl)) {
                pages.push({
                    url: page.pageUrl,
                    priority: 90,
                    source: 'navbar'
                });
                seenUrls.add(page.pageUrl);
            }
        }

        // Ajouter les autres pages (priorité basse)
        const otherPages = allPages.filter(
            page => !seenUrls.has(page.pageUrl) && page.pageUrl !== currentPageUrl
        );

        for (const page of otherPages) {
            pages.push({
                url: page.pageUrl,
                priority: 30,
                source: 'custom'
            });
            seenUrls.add(page.pageUrl);
        }

        // Trier par priorité (plus haute en premier)
        pages.sort((a, b) => b.priority - a.priority);

        return pages;
    }
}

// Export singleton
export const PagePreloadOrchestrator = new PagePreloadOrchestratorClass();

